import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format, formatDistanceToNow } from "date-fns";
import { id as localeId } from "date-fns/locale";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { 
  Siren, MapPin, ChevronRight, Loader2, AlertTriangle,
  Eye, FileSearch, Wrench, ArrowLeft, Save, Plus
} from "lucide-react";
import { ManualIncidentDialog } from "./ManualIncidentDialog";
import { 
  WorkplaceIncident, IncidentInvestigation, IncidentAction, IncidentStatus,
  InjurySeverity, INJURY_SEVERITY_LABELS, AREA_SAFETY_LABELS
} from "@/types/incident";
import { SiteFilter } from "./SiteFilter";
import { IncidentOverview } from "./incident/IncidentOverview";
import { InvestigationMethods, RCAMethod, RCAData } from "./investigation/InvestigationMethods";
import { IncidentActionForm } from "./incident/IncidentActionForm";

const STATUS_LABELS: Record<IncidentStatus, { label: string; color: string }> = {
  reported: { label: "Dilaporkan", color: "bg-blue-100 text-blue-800" },
  investigating: { label: "Investigasi", color: "bg-purple-100 text-purple-800" },
  actions_pending: { label: "Menunggu Aksi", color: "bg-amber-100 text-amber-800" },
  verification: { label: "Verifikasi", color: "bg-cyan-100 text-cyan-800" },
  closed: { label: "Ditutup", color: "bg-gray-100 text-gray-600" },
};

interface IncidentManagementProps {
  siteId?: string | null;
}

export function IncidentManagement({ siteId: propSiteId }: IncidentManagementProps = {}) {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedIncidentId = searchParams.get("incident");
  
  const [incidents, setIncidents] = useState<WorkplaceIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState<string>(propSiteId || "all");
  const [statusFilter, setStatusFilter] = useState<IncidentStatus | "all">("all");
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Selected incident details
  const [selectedIncident, setSelectedIncident] = useState<WorkplaceIncident | null>(null);
  const [investigation, setInvestigation] = useState<IncidentInvestigation | null>(null);
  const [actions, setActions] = useState<IncidentAction[]>([]);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Investigation form state
  const [rcaMethod, setRcaMethod] = useState<RCAMethod>("5_whys");
  const [rcaData, setRcaData] = useState<RCAData>({ method: "5_whys" });
  const [learningStatement, setLearningStatement] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (propSiteId) setSelectedSite(propSiteId);
  }, [propSiteId]);

  useEffect(() => {
    fetchIncidents();
    loadCompanyId();
  }, [selectedSite, statusFilter]);

  const loadCompanyId = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase
        .from("worker_profiles")
        .select("company_id")
        .eq("user_id", user.id)
        .single();
      if (data) setCompanyId(data.company_id);
    }
  };

  useEffect(() => {
    if (selectedIncidentId) {
      loadIncidentDetails(selectedIncidentId);
    } else {
      setSelectedIncident(null);
    }
  }, [selectedIncidentId]);

  const fetchIncidents = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("workplace_incidents")
        .select(`
          id, location, description, incident_datetime, injury_severity,
          area_safety_status, status, was_predictable, related_hazard_count,
          company_id, site_id, people_involved, photo_urls, body_part,
          mechanism_of_injury, immediate_treatment, lost_time_days, shift,
          lost_time_indicator, is_wajib_lapor, regulator_notified_at,
          created_at, updated_at, primary_report_id,
          site:company_locations!site_id(id, location_name),
          reporter:worker_profiles!reported_by(id, display_name)
        `)
        .order("incident_datetime", { ascending: false })
        .limit(100);

      if (selectedSite !== "all") {
        query = query.eq("site_id", selectedSite);
      }

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data } = await query;
      setIncidents((data || []) as WorkplaceIncident[]);
    } catch (error) {
      console.error("Error fetching incidents:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadIncidentDetails = async (incidentId: string) => {
    try {
      // Fetch incident
      const { data: incidentData } = await supabase
        .from("workplace_incidents")
        .select(`
          *,
          site:company_locations!site_id(id, location_name),
          reporter:worker_profiles!reported_by(id, display_name)
        `)
        .eq("id", incidentId)
        .single();

      if (incidentData) {
        setSelectedIncident(incidentData as unknown as WorkplaceIncident);
      }

      // Fetch investigation
      const { data: investigationData } = await supabase
        .from("incident_investigations")
        .select("*")
        .eq("incident_id", incidentId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (investigationData) {
        setInvestigation(investigationData as IncidentInvestigation);
        setRcaMethod((investigationData.rca_method as RCAMethod) || "5_whys");
        setRcaData((investigationData.rca_data as unknown as RCAData) || { method: "5_whys" });
        setLearningStatement(investigationData.learning_statement || "");
      } else {
        setInvestigation(null);
        setRcaMethod("5_whys");
        setRcaData({ method: "5_whys" });
        setLearningStatement("");
      }

      // Fetch actions
      const { data: actionsData } = await supabase
        .from("incident_actions")
        .select(`
          *,
          owner:worker_profiles!owner_id(id, display_name)
        `)
        .eq("incident_id", incidentId)
        .order("created_at", { ascending: true });

      setActions((actionsData || []) as IncidentAction[]);
    } catch (error) {
      console.error("Error loading incident details:", error);
    }
  };

  const handleSaveInvestigation = async () => {
    if (!selectedIncident || !learningStatement.trim()) {
      toast({ title: "Pernyataan pembelajaran wajib diisi", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({ title: "Anda harus login", variant: "destructive" });
        return;
      }

      // Look up the worker_profiles.id for this auth user
      const { data: workerProfile, error: profileError } = await supabase
        .from("worker_profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (profileError || !workerProfile) {
        toast({ title: "Profil pekerja tidak ditemukan", variant: "destructive" });
        return;
      }

      // Call edge function to save investigation with AI rewrite
      const { data, error } = await supabase.functions.invoke("submit-incident-investigation", {
        body: {
          incident_id: selectedIncident.id,
          investigated_by: workerProfile.id, // Use worker_profiles.id, not auth user ID
          failed_controls: extractFailedControls(rcaData),
          contributing_conditions: extractContributingConditions(rcaData),
          learning_statement: learningStatement,
          rca_method: rcaMethod,
          rca_data: rcaData,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: "Investigasi berhasil disimpan" });
      loadIncidentDetails(selectedIncident.id);
    } catch (error) {
      console.error("Error saving investigation:", error);
      toast({ 
        title: "Gagal menyimpan investigasi", 
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive" 
      });
    } finally {
      setSaving(false);
    }
  };

  // Extract failed controls from RCA data for backward compatibility
  const extractFailedControls = (data: RCAData): string[] => {
    const controls: string[] = [];
    
    if (data.fishbone) {
      if ((data.fishbone.man || []).length > 0) controls.push("training", "supervision");
      if ((data.fishbone.machine || []).length > 0) controls.push("engineering");
      if ((data.fishbone.method || []).length > 0) controls.push("procedure");
    }
    
    if (data.icam?.absentDefenses) {
      if (data.icam.absentDefenses.includes("Engineering controls")) controls.push("engineering");
      if (data.icam.absentDefenses.includes("Administrative controls")) controls.push("procedure");
      if (data.icam.absentDefenses.includes("Supervision")) controls.push("supervision");
    }

    if (data.swissCheese?.layers) {
      data.swissCheese.layers.forEach(layer => {
        if (layer.holes.length > 0) {
          if (layer.name.includes("Supervision")) controls.push("supervision");
          if (layer.name.includes("Organizational")) controls.push("planning");
        }
      });
    }
    
    return [...new Set(controls)];
  };

  const extractContributingConditions = (data: RCAData): string[] => {
    const conditions: string[] = [];
    
    if (data.icam?.taskConditions) {
      if (data.icam.taskConditions.includes("Time pressure")) conditions.push("time_pressure");
      if (data.icam.taskConditions.includes("Workload")) conditions.push("work_overload");
      if (data.icam.taskConditions.includes("Environmental factors")) conditions.push("environmental");
    }
    
    if (data.fishbone?.environment && data.fishbone.environment.length > 0) {
      conditions.push("environmental");
    }
    
    return [...new Set(conditions)];
  };

  const handleSelectIncident = (incidentId: string) => {
    setSearchParams({ tab: "incident-management", incident: incidentId });
  };

  const handleBackToList = () => {
    setSearchParams({ tab: "incident-management" });
    setSelectedIncident(null);
  };

  const getSeverityColor = (severity: InjurySeverity) => {
    const colors: Record<InjurySeverity, string> = {
      no_injury: "bg-green-100 text-green-800",
      first_aid: "bg-yellow-100 text-yellow-800",
      medical_treatment: "bg-orange-100 text-orange-800",
      lost_time: "bg-red-100 text-red-800",
      fatality: "bg-gray-900 text-white",
    };
    return colors[severity] || "bg-muted";
  };

  if (loading && incidents.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Detail View
  if (selectedIncident) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={handleBackToList}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Siren className="h-5 w-5 text-primary" />
                Incident Management
              </h2>
              <p className="text-sm text-muted-foreground">
                {selectedIncident.location} • {format(new Date(selectedIncident.incident_datetime), "dd MMM yyyy HH:mm")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getSeverityColor(selectedIncident.injury_severity as InjurySeverity)}>
              {INJURY_SEVERITY_LABELS[selectedIncident.injury_severity as InjurySeverity]?.labelId}
            </Badge>
            <Badge className={STATUS_LABELS[selectedIncident.status as IncidentStatus]?.color}>
              {STATUS_LABELS[selectedIncident.status as IncidentStatus]?.label}
            </Badge>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="overview" className="gap-2">
              <Eye className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="investigation" className="gap-2">
              <FileSearch className="h-4 w-4" />
              Investigasi
            </TabsTrigger>
            <TabsTrigger value="actions" className="gap-2">
              <Wrench className="h-4 w-4" />
              Tindakan
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <IncidentOverview 
              incident={selectedIncident} 
              onRefresh={() => loadIncidentDetails(selectedIncident.id)} 
            />
          </TabsContent>

          <TabsContent value="investigation" className="mt-6">
            <div className="space-y-6">
              {investigation && (
                <Card className="bg-success/5 border-success/30">
                  <CardContent className="p-4">
                    <p className="text-success font-medium">
                      ✓ Investigasi telah dilakukan pada {format(new Date(investigation.investigated_at), "dd MMM yyyy HH:mm")}
                    </p>
                    {investigation.learning_statement_ai_rewritten && (
                      <p className="text-sm mt-2">
                        <strong>Pembelajaran:</strong> {investigation.learning_statement_ai_rewritten}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}

              <InvestigationMethods
                selectedMethod={rcaMethod}
                onMethodChange={(m) => {
                  setRcaMethod(m);
                  setRcaData({ ...rcaData, method: m });
                }}
                data={rcaData}
                onDataChange={setRcaData}
                learningStatement={learningStatement}
                onLearningStatementChange={setLearningStatement}
                readonly={!!investigation}
              />

              {!investigation && (
                <Button onClick={handleSaveInvestigation} disabled={saving} className="w-full gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {saving ? "Menyimpan..." : "Simpan Investigasi"}
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="actions" className="mt-6">
            <IncidentActionForm
              incidentId={selectedIncident.id}
              companyId={selectedIncident.company_id}
              siteId={selectedIncident.site_id || undefined}
              actions={actions}
              onRefresh={() => loadIncidentDetails(selectedIncident.id)}
            />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Siren className="h-6 w-6 text-primary" />
            Incident Management
          </h2>
          <p className="text-muted-foreground">
            Investigasi dan tindakan korektif/preventif
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button onClick={() => setShowManualDialog(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Tambah Manual
          </Button>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as IncidentStatus | "all")}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              <SelectItem value="reported">Dilaporkan</SelectItem>
              <SelectItem value="investigating">Investigasi</SelectItem>
              <SelectItem value="actions_pending">Menunggu Aksi</SelectItem>
              <SelectItem value="verification">Verifikasi</SelectItem>
              <SelectItem value="closed">Ditutup</SelectItem>
            </SelectContent>
          </Select>
          {!propSiteId && <SiteFilter value={selectedSite} onChange={setSelectedSite} />}
        </div>
      </div>

      {/* Manual Incident Dialog */}
      {companyId && (
        <ManualIncidentDialog
          open={showManualDialog}
          onOpenChange={setShowManualDialog}
          companyId={companyId}
          siteId={selectedSite !== "all" ? selectedSite : undefined}
          onSuccess={() => {
            fetchIncidents();
            setShowManualDialog(false);
          }}
        />
      )}

      {/* Incident List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Daftar Insiden ({incidents.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {incidents.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              Tidak ada insiden ditemukan
            </div>
          ) : (
            <div className="divide-y">
              {incidents.map((incident, idx) => {
                const severityInfo = INJURY_SEVERITY_LABELS[incident.injury_severity as InjurySeverity];
                const statusInfo = STATUS_LABELS[incident.status as IncidentStatus];
                const isUrgent = incident.area_safety_status === "still_unsafe";
                
                return (
                  <motion.div
                    key={incident.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.02 }}
                    onClick={() => handleSelectIncident(incident.id)}
                    className={cn(
                      "p-4 cursor-pointer hover:bg-muted/50 transition-colors",
                      isUrgent && "bg-destructive/5"
                    )}
                  >
                    <div className="flex items-center gap-4">
                      {/* Severity Icon */}
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-lg",
                        getSeverityColor(incident.injury_severity as InjurySeverity)
                      )}>
                        {severityInfo?.icon || "⚠️"}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge className={getSeverityColor(incident.injury_severity as InjurySeverity)}>
                            {severityInfo?.labelId}
                          </Badge>
                          <Badge className={statusInfo?.color}>
                            {statusInfo?.label}
                          </Badge>
                          {isUrgent && (
                            <Badge variant="destructive" className="animate-pulse">
                              🚨 Masih Berbahaya
                            </Badge>
                          )}
                        </div>
                        
                        <p className="font-medium line-clamp-1">
                          {incident.description || incident.location}
                        </p>
                        
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {incident.location}
                          </span>
                          <span>•</span>
                          <span>
                            {formatDistanceToNow(new Date(incident.incident_datetime), { 
                              addSuffix: true, 
                              locale: localeId 
                            })}
                          </span>
                          {incident.was_predictable && (
                            <>
                              <span>•</span>
                              <span className="text-amber-600 flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Dapat Diprediksi
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
